import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationAction,
  AutomationActionStatus,
  AutomationStatus,
  ExperienceActorType,
  ExperienceStatus,
  ExperienceUserActor,
  getSubTaskTemplates,
  IAutomation,
  IAutomationTemplate,
  IExperience,
  IExperienceTemplate,
  isEventDrivenTiming,
  ISubTask,
  ISupportingActor,
  ITask,
  ITaskTemplate,
  MessageStatus,
  RequirementType,
  SubTaskStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { every, get, keyBy } from 'lodash';
import shortid from 'shortid';
import { addToTs, now, Timestamp, tsFromMillis, tsIsAfter } from '../time';
import { matchUser } from '../user';
import {
  filterBlocksForAudience,
  getAudienceById,
  getAudienceForUser,
  getEffectiveAudiences
} from './audience';
import {
  areAllTasksDone,
  generateAccessToken,
  getActorForKey,
  getEarliestDateInInstance,
  isTaskDone,
  isTaskSkipped
} from './instance';
import {
  getAllMessageTemplates,
  skipAllBecauseCatchupModeIsDisabled
} from './template';
import { getFreshNonTaskStartDate, getFreshTaskDates } from './timing';

const logger = console;

export const computeExpectedEndForInstance = (
  inst: Doc<IExperience>
): Timestamp => {
  const { startsAt, tasks, messagesStatuses, automations } = inst.data;
  return tsFromMillis(
    Math.max(
      ...tasks.map((t) => t.timing.end.toMillis()),
      ...Object.entries(messagesStatuses).map((m) => m[1].timing.at.toMillis()),
      ...Object.entries(automations).map((a) => a[1].timing.at.toMillis()),
      startsAt.toMillis() // fallback
    )
  );
};

const areAnyTasksLate = (tasks: ITask[]) => {
  return tasks.some((t) => t.status === TaskStatus.LATE);
};

const isOptionalTask = (task: ITask) => {
  return every(
    task.subTasks,
    (s) => s.requirementType === RequirementType.OPTIONAL
  );
};

export const computeTaskStatus = (
  taskTpl: ITaskTemplate,
  task: ITask,
  n: Timestamp
) => {
  if (isTaskSkipped(task)) {
    return TaskStatus.SKIPPED;
  }
  const audience = getAudienceById(taskTpl.audiences, task.audience);
  if (isTaskDone(task, audience.completionCriteria, taskTpl)) {
    return TaskStatus.COMPLETE;
  }
  if (tsIsAfter(n, task.timing.end)) {
    const isOptional = isOptionalTask(task);
    return isOptional ? TaskStatus.PAST_DEADLINE : TaskStatus.LATE;
  }
  if (tsIsAfter(task.timing.start, n)) {
    return TaskStatus.NOT_STARTED;
  }
  return TaskStatus.RUNNING;
};

export const computeTaskStatusButKeepNotStartedState = (
  taskTpl: ITaskTemplate,
  task: ITask,
  n: Timestamp
) => {
  // this can happen e.g. when a new supporting actor is added - all these items
  // appear NOT_STARTED. If we then update the template, status would start shifting
  // We don't want that immediately and let the system catch up the not started
  // items on its own first.
  const nextStatus = computeTaskStatus(taskTpl, task, n);
  if (
    task.status === TaskStatus.NOT_STARTED &&
    nextStatus !== TaskStatus.COMPLETE &&
    nextStatus !== TaskStatus.SKIPPED
  ) {
    return task.status;
  }
  return nextStatus;
};

export const computeAutomationStatus = (automation: IAutomation) => {
  const actionStatuses = Object.values(automation.actions).map((a) => a.status);
  return actionStatuses.some((s) => s === AutomationActionStatus.ERROR)
    ? AutomationStatus.ERROR
    : actionStatuses.every((s) => s === AutomationActionStatus.SKIPPED)
    ? AutomationStatus.SKIPPED
    : actionStatuses.every(
        (s) =>
          s === AutomationActionStatus.DONE ||
          s === AutomationActionStatus.SKIPPED
      )
    ? AutomationStatus.DONE
    : AutomationStatus.UPCOMING;
};

const getExperiecneActorFromSupportingActorDefinition = (
  participant: Doc<IUser>,
  a: ISupportingActor,
  referenceDate: Timestamp
): ExperienceUserActor | null => {
  if (a.type === 'staticUser') {
    const uId =
      a.ruleBasedUserIds.find((r) =>
        matchUser(participant, r.rule, referenceDate)
      )?.userId || a.defaultUserId;
    if (uId) {
      return {
        type: ExperienceActorType.USER,
        userId: uId,
        key: a.key,
        accessToken: generateAccessToken()
      };
    }
  }
  if (a.type === 'user' && a.source === 'PROFILE') {
    const otherUserId = get(participant.data as any, a.field);
    if (otherUserId && typeof otherUserId === 'string') {
      return {
        type: ExperienceActorType.USER,
        userId: otherUserId,
        key: a.key,
        accessToken: generateAccessToken()
      };
    }
  }
  logger.warn('missing supporting actor', {
    supportingActor: a,
    participant
  });
  return null;
};

// This is only required until we have soft deletions
const cleanupSubTasks = (
  tTpl: ITaskTemplate,
  task: ITask,
  n: Timestamp
): ITask => {
  const presentSubTasksTplIds = new Set(
    getSubTaskTemplates(tTpl).map((s) => s.id)
  );
  const nextSubTasks = task.subTasks.filter((s) =>
    presentSubTasksTplIds.has(s.templateId)
  );

  const nextTask = {
    ...task,
    subTasks: nextSubTasks
  };

  // This can still break - as we're not recomputing the audience, the completion
  // criteria for this audience might now be out of sync with what the task contains
  nextTask.status = computeTaskStatusButKeepNotStartedState(tTpl, task, n);
  return nextTask;
};

// This is only required until we have soft deletions
const cleanupAutomationActions = (
  aTpl: IAutomationTemplate,
  automation: IAutomation
): IAutomation => {
  const nextActions: IAutomation['actions'] = { ...automation.actions };
  const actionTplIds = new Set(aTpl.actions.map((x) => x.id));
  Object.keys(nextActions).forEach((aTplId) => {
    if (!actionTplIds.has(aTplId)) {
      delete nextActions[aTplId];
    }
  });

  const nextAutomation: IAutomation = { ...automation, actions: nextActions };
  nextAutomation.status = computeAutomationStatus(nextAutomation);

  if (
    nextAutomation.status === AutomationStatus.DONE ||
    nextAutomation.status === AutomationStatus.ERROR
  ) {
    nextAutomation.completedAt = nextAutomation.completedAt || now();
  }
  return nextAutomation;
};

export enum InstanceRecalculateReason {
  TEMPLATE_CHANGE = 'TEMPLATE_CHANGE',
  INSTANCE_CHANGE = 'INSTANCE_CHANGE'
}

export const __recalculateExperienceInstanceAtPointInTimeWithoutSideEffects = (
  tpl: Doc<IExperienceTemplate>,
  instance: Doc<IExperience>,
  participant: Doc<IUser>,
  n: Timestamp,
  recalculateReason: InstanceRecalculateReason,
  opts: { honorCatchupModeSettings?: boolean } = {}
) => {
  const { anchorDate } = instance.data;
  const { anchor, tz } = tpl.data;

  // TODO when new supporting actors appear, they also need to be added
  // to the actor list - means that they need to be looked up though!

  // Traverse the template for all items
  // Check against the instance timing to see if it's necessary
  // to make updates
  // Special cases:
  // - new items -> check if they would need to happen in the future and add them if need be
  // - deleted items -> ideally a soft deletion approach would deal with this, as we could still
  //                    just iterate over the template. Until we have that we also need to iterate
  //                    from the other side (which is removing all items we don't know about anymore -
  //                    no matter if it's future or present.

  const nextInstance: Doc<IExperience> = {
    ...instance,
    data: {
      ...instance.data,
      tasks: [],
      messagesStatuses: {},
      automations: {},

      userIds: [...instance.data.userIds],
      accessTokens: [...instance.data.accessTokens],
      actors: [...instance.data.actors]
    }
  };

  const earliestDateInInstance = getEarliestDateInInstance(instance);

  tpl.data.tasks.forEach((taskTpl) => {
    const prevTask = instance.data.tasks.find(
      (t) => t.templateId === taskTpl.id
    );
    if (!prevTask) {
      // this is a new task!
      if (isEventDrivenTiming(taskTpl.timing)) {
        return null;
      }
      const newTaskTiming = getFreshTaskDates(
        taskTpl.timing,
        anchorDate.at,
        anchor,
        tz
      );
      if (tsIsAfter(n, newTaskTiming.start)) {
        return;
      }
      // Create a new task
      // - check if we know this actor already
      // - if yes, we're fine
      // - if no, look up it and see if we can find it
      //   - if yes, add to actors list
      //   - if no, do not add the task
      const actor = getActorForKey(instance, taskTpl.actor);
      if (!actor) {
        const supportingActorDef = tpl.data.supportingActors[taskTpl.actor];
        if (!supportingActorDef) {
          logger.log('No supporting actor definition found', {
            actor: taskTpl.actor,
            supportinActors: tpl.data.supportingActors,
            taskTplId: taskTpl.id,
            tplId: tpl.id,
            expId: instance.id
          });
          return;
        }
        const newActor = getExperiecneActorFromSupportingActorDefinition(
          participant,
          supportingActorDef,
          anchorDate.at
        );

        // TODO This doesn't check whether this is actually a user or not
        // (could be deleted, not found etc);
        // So we might get a new actor here, that really shouldn't be there.
        // This explains also some of the things I saw e.g. within CA!

        if (!newActor) {
          logger.log('No actor for this task', {
            actor: taskTpl.actor,
            participant,
            taskTplId: taskTpl.id,
            tplId: tpl.id,
            expId: instance.id
          });
          return;
        }
        nextInstance.data.actors.push(newActor);
        nextInstance.data.accessTokens.push(newActor.accessToken);
        nextInstance.data.userIds.push(newActor.userId);
      }

      const audience = getAudienceForUser(
        getEffectiveAudiences(taskTpl),
        participant,
        anchorDate.at
      );
      const subTasks = filterBlocksForAudience(
        getSubTaskTemplates(taskTpl),
        audience
      );
      if (!subTasks.length) {
        logger.info('NO_SUBTASKS - SKIPPING TASK', {
          taskTemplate: taskTpl,
          experienceId: instance.id,
          audience
        });
        return;
      }
      const nextTask: ITask = {
        id: shortid(),
        experienceId: instance.id,
        templateId: taskTpl.id,
        status: TaskStatus.NOT_STARTED,
        actor: taskTpl.actor,
        overrides: null,
        audience: audience.id,
        subTasks: subTasks.map<ISubTask>((s) => ({
          id: shortid(),
          templateId: s.id,
          type: s.type,
          status: SubTaskStatus.OPEN,
          completedAt: null,
          requirementType: s.requirementType,
          parentLayoutElement: s.parentLayoutElement || null
        })),
        timing: {
          ...getFreshTaskDates(
            taskTpl.timing,
            anchorDate.at,
            tpl.data.anchor,
            tpl.data.tz
          ),
          manualOverride: false
        }
      };
      // New tasks are always NOT_STARTED
      // nextTask.status = computeTaskStatus(taskTpl, nextTask, n);

      // TODO if the task now completed, we need to generate a completion event
      console.log('adding new task', nextTask);
      nextInstance.data.tasks.push(nextTask);
      return;
    } else {
      if (tsIsAfter(n, prevTask.timing.start)) {
        nextInstance.data.tasks.push(cleanupSubTasks(taskTpl, prevTask, n));
        // TODO - this can leave tasks hanging.
        // All subtasks might be removed from it and they end up being empty tasks
        // Automations might be affected by the same problem!
        return;
      }
      // recompute the task (its timing, its audience, its content)
      const audience = getAudienceForUser(
        getEffectiveAudiences(taskTpl),
        participant,
        anchorDate.at
      );
      const subTasks = filterBlocksForAudience(
        getSubTaskTemplates(taskTpl),
        audience
      );

      // Controversial - but it's not clear how tasks without anything to do
      // should behave. We might change this stance once we have a better sense of optinality
      // of tasks
      if (!subTasks.length) {
        logger.info('NO_SUBTASKS - SKIPPING TASK', {
          taskTemplate: taskTpl,
          experienceId: instance.id,
          audience
        });
        return;
      }

      const nextTiming =
        prevTask.timing.manualOverride || isEventDrivenTiming(taskTpl.timing) // TODO Triggered Items - controversial. can previous triggered tasks move? - most likely not, since they are guaranteed to have already started at this point!
          ? prevTask.timing
          : {
              ...getFreshTaskDates(
                taskTpl.timing,
                anchorDate.at,
                tpl.data.anchor,
                tpl.data.tz
              ),
              manualOverride: false
            };

      const prevSubTasksByTplId = keyBy(prevTask.subTasks, (s) => s.templateId);
      const nextSubTasks = subTasks.map<ISubTask>((s) => {
        const prevSubTask = prevSubTasksByTplId[s.id];
        return {
          id: prevSubTask?.id || shortid(),
          templateId: s.id,
          type: s.type,
          status:
            opts.honorCatchupModeSettings && taskTpl.catchup.disabled
              ? skipAllBecauseCatchupModeIsDisabled(
                  taskTpl,
                  now(),
                  nextTiming.start
                )
                ? SubTaskStatus.SKIPPED
                : SubTaskStatus.OPEN
              : prevSubTask?.status || SubTaskStatus.OPEN,
          completedAt: prevSubTask?.completedAt || null,
          requirementType: s.requirementType,
          parentLayoutElement: s.parentLayoutElement || null
        };
      });

      const nextTask: ITask = {
        id: prevTask.id,
        experienceId: instance.id,
        templateId: taskTpl.id,
        status: TaskStatus.NOT_STARTED, // update status, based on prev status and new subtasks
        actor: taskTpl.actor,
        overrides: null,
        audience: audience.id,
        subTasks: nextSubTasks,
        timing: nextTiming
      };

      if (tsIsAfter(n, nextTask.timing.start)) {
        if (tsIsAfter(earliestDateInInstance, nextTask.timing.start)) {
          const duration =
            nextTask.timing.end.toMillis() - nextTask.timing.start.toMillis();
          nextTask.timing.start = earliestDateInInstance;
          nextTask.timing.end = addToTs(earliestDateInInstance, duration);
        }
        // if the task would be moved to the past, leave it untouched
        // TODO - this is somewhat controversial
        // console.log('NOT MOVING TO THE PAST', prevTask);
        // nextInstance.data.tasks.push(cleanupSubTasks(taskTpl, prevTask, n));
        // return;
      }

      nextTask.status = computeTaskStatusButKeepNotStartedState(
        taskTpl,
        nextTask,
        n
      );
      nextInstance.data.tasks.push(Object.assign({}, prevTask, nextTask));
      return;
    }
  });

  getAllMessageTemplates(tpl.data.messages).forEach((msgTpl) => {
    const prevMsg = instance.data.messagesStatuses[msgTpl.id];
    if (!prevMsg) {
      if (isEventDrivenTiming(msgTpl.timing)) {
        return;
      }
      const nextMsgTiming = getFreshNonTaskStartDate(
        msgTpl.timing,
        anchorDate.at,
        anchor,
        tz
      );
      if (tsIsAfter(n, nextMsgTiming)) {
        return;
      }
      // this is a new msg!
      const newMsgTiming = getFreshNonTaskStartDate(
        msgTpl.timing,
        anchorDate.at,
        anchor,
        tz
      );
      if (tsIsAfter(n, newMsgTiming)) {
        return;
      }

      // Create a new msg
      // - check if we know this actor already
      // - if yes, we're fine
      // - if no, look up it and see if we can find it
      //   - if yes, add to actors list
      //   - if no, do not add the msg
      const actor = getActorForKey(instance, msgTpl.actor);
      if (!actor) {
        const supportingActorDef = tpl.data.supportingActors[msgTpl.actor];
        if (!supportingActorDef) {
          logger.log('No supporting actor definition found', {
            actor: msgTpl.actor,
            supportinActors: tpl.data.supportingActors,
            msgTplId: msgTpl.id,
            tplId: tpl.id,
            expId: instance.id
          });
          return;
        }
        const newActor = getExperiecneActorFromSupportingActorDefinition(
          participant,
          supportingActorDef,
          anchorDate.at
        );
        if (!newActor) {
          logger.log('No actor for this msg', {
            actor: msgTpl.actor,
            participant,
            msgTplId: msgTpl.id,
            tplId: tpl.id,
            expId: instance.id
          });
          return;
        }
        nextInstance.data.actors.push(newActor);
        nextInstance.data.accessTokens.push(newActor.accessToken);
        nextInstance.data.userIds.push(newActor.userId);
      }

      const audience = getAudienceForUser(
        getEffectiveAudiences(msgTpl),
        participant,
        anchorDate.at
      );
      if (!audience.blocks.length) {
        logger.info('NO_BLOCKS - SKIPPING MSG', {
          msg: msgTpl,
          experienceId: instance.id,
          audience
        });
        return;
      }
      nextInstance.data.messagesStatuses[msgTpl.id] = {
        status: MessageStatus.UPCOMING,
        overrides: { at: null },
        timing: {
          at: nextMsgTiming,
          manualOverride: false
        },
        audience: audience.id
      };
      return;
    } else {
      if (tsIsAfter(n, prevMsg.timing.at)) {
        nextInstance.data.messagesStatuses[msgTpl.id] = prevMsg;
        return;
      }

      const audience = getAudienceForUser(
        getEffectiveAudiences(msgTpl),
        participant,
        anchorDate.at
      );
      if (!audience.blocks.length) {
        logger.info('NO_BLOCKS - SKIPPING MSG', {
          msg: msgTpl,
          experienceId: instance.id,
          audience
        });
        return;
      }

      const nextTiming =
        prevMsg.timing.manualOverride || isEventDrivenTiming(msgTpl.timing)
          ? prevMsg.timing
          : {
              at: getFreshNonTaskStartDate(
                msgTpl.timing,
                anchorDate.at,
                anchor,
                tz
              ),
              manualOverride: false
            };

      const nextMessage = {
        status:
          opts.honorCatchupModeSettings && msgTpl.catchup.disabled
            ? skipAllBecauseCatchupModeIsDisabled(msgTpl, now(), nextTiming.at)
              ? MessageStatus.SKIPPED
              : MessageStatus.UPCOMING
            : prevMsg.status || MessageStatus.UPCOMING, // need to check previous status for e.g. SKIPPED items
        overrides: { at: null },
        timing: nextTiming,
        markedAsRead: prevMsg.markedAsRead || null,
        audience: audience.id
      };
      if (tsIsAfter(n, nextMessage.timing.at)) {
        if (tsIsAfter(earliestDateInInstance, nextMessage.timing.at)) {
          nextMessage.timing.at = earliestDateInInstance;
        }

        // if the message would be moved to the past, leave it untouched
        // TODO - this is somewhat controversial
        // nextInstance.data.messagesStatuses[msgTpl.id] = prevMsg;
        // return;
      }

      nextInstance.data.messagesStatuses[msgTpl.id] = nextMessage;
      return;
    }
  });

  tpl.data.automations.forEach((autTpl) => {
    const prevAut = instance.data.automations[autTpl.id];
    if (!prevAut) {
      if (isEventDrivenTiming(autTpl.timing)) {
        return;
      }
      const nextAutTiming = getFreshNonTaskStartDate(
        autTpl.timing,
        anchorDate.at,
        anchor,
        tz
      );
      if (tsIsAfter(n, nextAutTiming)) {
        return;
      }
      // Add a new automation
      const audience = getAudienceForUser(
        getEffectiveAudiences(autTpl),
        participant,
        anchorDate.at
      );
      if (!audience.blocks.length) {
        logger.info('NO_BLOCKS - SKIPPING AUTOMATIOn', {
          automatonTpl: autTpl,
          experienceId: instance.id,
          audience
        });
        return;
      }
      nextInstance.data.automations[autTpl.id] = {
        id: shortid(),
        experienceId: instance.id,
        templateId: autTpl.id,
        status: AutomationStatus.UPCOMING,
        completedAt: null,
        overrides: null,
        timing: {
          at: nextAutTiming,
          manualOverride: false
        },
        audience: audience.id,
        actions: keyBy(
          autTpl.actions
            .filter((b) => audience.blocks.includes(b.id))
            .map<AutomationAction>((ac) => ({
              id: shortid(),
              templateId: ac.id,
              type: ac.type,
              status: AutomationActionStatus.UPCOMING,
              data: null,
              errorData: null,
              doneAt: null
            })),
          (ac) => ac.templateId
        )
      };
    } else {
      if (tsIsAfter(n, prevAut.timing.at)) {
        nextInstance.data.automations[autTpl.id] = cleanupAutomationActions(
          autTpl,
          prevAut
        );
        return;
      }
      const audience = getAudienceForUser(
        getEffectiveAudiences(autTpl),
        participant,
        anchorDate.at
      );
      if (!audience.blocks.length) {
        logger.info('NO_BLOCKS - SKIPPING AUTOMATIOn', {
          automatonTpl: autTpl,
          experienceId: instance.id,
          audience
        });
        return;
      }

      const nextTiming =
        prevAut.timing.manualOverride || isEventDrivenTiming(autTpl.timing)
          ? prevAut.timing
          : {
              at: getFreshNonTaskStartDate(
                autTpl.timing,
                anchorDate.at,
                anchor,
                tz
              ),
              manualOverride: false
            };

      const nextAutomation: IAutomation = {
        id: prevAut.id,
        experienceId: instance.id,
        templateId: autTpl.id,
        status: AutomationStatus.UPCOMING, // will be determined in next step
        completedAt: null,
        overrides: null,
        timing: nextTiming,
        audience: audience.id,
        actions: keyBy(
          autTpl.actions
            .filter((b) => audience.blocks.includes(b.id))
            .map<AutomationAction>((ac) => {
              const prevAction = prevAut.actions[ac.id];
              // If type changed, always return a new action
              const nextStatus =
                opts.honorCatchupModeSettings && autTpl.catchup.disabled
                  ? skipAllBecauseCatchupModeIsDisabled(
                      autTpl,
                      now(),
                      nextTiming.at
                    )
                    ? AutomationActionStatus.SKIPPED
                    : AutomationActionStatus.UPCOMING
                  : prevAction?.status || AutomationActionStatus.UPCOMING;
              if (!prevAction || prevAction.type !== ac.type) {
                return {
                  id: shortid(),
                  templateId: ac.id,
                  type: ac.type,
                  status: nextStatus,
                  data: null,
                  errorData: null,
                  doneAt: null
                };
              }
              return { ...prevAction, status: nextStatus };
            }),
          (ac) => ac.templateId
        )
      };

      if (tsIsAfter(n, nextAutomation.timing.at)) {
        if (tsIsAfter(earliestDateInInstance, nextAutomation.timing.at)) {
          nextAutomation.timing.at = earliestDateInInstance;
        }
        // if the automation would be moved to the past, leave it untouched
        // TODO - this is somewhat controversial
        // nextInstance.data.automations[
        //   nextAutomation.templateId
        // ] = cleanupAutomationActions(autTpl, prevAut);
        // return;
      }

      const nextStatus = computeAutomationStatus(nextAutomation);
      if (
        nextStatus === AutomationStatus.DONE ||
        nextStatus === AutomationStatus.ERROR
      ) {
        nextAutomation.completedAt = prevAut.completedAt || now();
      }
      // TODO create events so that we track timings for further analysis
      nextAutomation.status = nextStatus;
      nextInstance.data.automations[nextAutomation.templateId] = nextAutomation;
    }
  });

  // Removal is not required anymore, as we gradually built up the new object anyway
  // Double check for any hard removals until we have soft deletions in place
  // const taskTplIds = tpl.data.tasks.map((tTpl) => tTpl.id);
  // const msgTplIds = getAllMessageTemplates(tpl.data.messages).map(
  //   (mTpl) => mTpl.id
  // );
  // const autTplIds = tpl.data.automations.map((aTpl) => aTpl.id);
  // instance.data.tasks = instance.data.tasks.filter((t) => {
  //   return taskTplIds.includes(t.templateId);
  // });
  // Object.keys(instance.data.messagesStatuses).forEach((msgId) => {
  //   if (!msgTplIds.includes(msgId)) {
  //     delete instance.data.messagesStatuses[msgId];
  //   }
  // });
  // Object.keys(instance.data.automations).forEach((autId) => {
  //   if (!autTplIds.includes(autId)) {
  //     delete instance.data.automations[autId];
  //   }
  // });

  nextInstance.data.expectedEnd = computeExpectedEndForInstance(nextInstance);
  const isInstanceDone = areAllTasksDone(nextInstance.data.tasks);
  const anyTasksLate = areAnyTasksLate(nextInstance.data.tasks);
  if (isInstanceDone) {
    nextInstance.data.status = ExperienceStatus.COMPLETE;
    nextInstance.data.finishedAt = instance.data.finishedAt || now();
  }
  if (anyTasksLate) {
    // TODO what to do if instance was aborted?
    nextInstance.data.status = ExperienceStatus.LATE;
  }

  if (
    (nextInstance.data.status === ExperienceStatus.COMPLETE &&
      !isInstanceDone) ||
    (nextInstance.data.status === ExperienceStatus.LATE && !anyTasksLate)
  ) {
    nextInstance.data.status = ExperienceStatus.RUNNING;
  }

  return nextInstance;
};
